/* eslint-disable react/no-multi-comp */
import React, { useState, useEffect } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";
import { makeStyles } from "@mui/styles";
import {
  Drawer,
  List,
  ListSubheader,
  Hidden,
  Divider,
  Button,
  ButtonGroup,
  Typography,
  FormControl,
  Select,
  TextField,
  MenuItem,
} from "@mui/material";
import { setWorkOrderFilters } from "slices/filtersSlice";
import { setCurrentType } from "slices/currentTypeSlice";
import {
  clearAssembliesPagination,
  clearDevicesPagination,
  clearWorkOrderPagination,
} from "slices/paginationSlice";

import { useLocation, useNavigate } from "react-router-dom";
import { clearDeviceWorkOrders } from "slices/deviceWorkOrdersSlice";
import { clearAssemblyWorkOrders } from "slices/assemblyWorkOrdersSlice";
import packageJson from "../../../package.json";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  mobileDrawer: {
    width: 256,
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: "calc(100% - 64px)",
  },
  navigation: {
    overflow: "auto",
    padding: theme.spacing(0, 2, 2, 2),
    flexGrow: 1,
  },
  type: {
    overflow: "hidden",
    padding: theme.spacing(0, 2, 2, 2),
    flexGrow: 1,
  },
  profile: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
  },
  details: {
    marginLeft: theme.spacing(2),
  },
  moreButton: {
    marginLeft: "auto",
  },
  groupButtons: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 70,
  },
  div: {
    width: "100%",
    display: "flex",
  },
  divChild: {
    flex: "1",
    margin: "5px",
  },
  textField: {
    marginTop: theme.spacing(3),
    width: 150,
  },
  appVersion: {
    margin: theme.spacing(0, 2, 1, 2),
    textAlign: "center",
  },
  infoButtonContainer: {
    position: "relative",
    display: "inline-block",
  },
  infoText: {
    visibility: "hidden",
    width: "230px", // Adjust width as needed
    backgroundColor: "lightgray",
    color: "#333",
    textAlign: "center",
    borderRadius: "6px",
    padding: "8px",
    position: "absolute",
    zIndex: 1,
    bottom: "100%", // Adjusts the position above the button
    left: "50%",
    marginLeft: "-60px", // Centers the text
    opacity: 0,
    transition: "opacity 0.3s, visibility 0.3s",
    fontSize: "14px", // Adjust font size
  },
  infoButtonContainerHover: {
    "&:hover $infoText": {
      visibility: "visible",
      opacity: 1,
    },
  },
}));

function NavBar({ openMobile, onMobileClose, className, ...rest }) {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [btnStyleType, setBtnStyleType] = useState(0);

  const [workOrderFilterState, setWorkOrderFilterState] = useState({
    startDate: null,
    endDate: null,
    productType: null,
  });

  const workOrderFilters = rest.filtersState.workOrderFilters;
  const currentType = rest.currentType;

  const handleChangeProductType = (event) => {
    let productType = event.target.value;
    setWorkOrderFilterState({
      ...workOrderFilterState,
      productType: productType,
    });
  };

  const handleChange = (event) => {
    event.persist();

    setWorkOrderFilterState((prevFilteredState) => ({
      ...prevFilteredState,
      [event.target.name]: event.target.value,
    }));
  };

  const handleChangeAssemblies = (event) => {
    event.persist();

    setWorkOrderFilterState((prevFilteredState) => ({
      ...prevFilteredState,
      [event.target.name]: event.target.value,
    }));
  };

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }

    // eslint-disable-next-line
  }, []); //??

  useEffect(() => {
    dispatch(clearDeviceWorkOrders());
    dispatch(clearWorkOrderPagination());
    dispatch(
      setWorkOrderFilters({
        ...workOrderFilters,
        startDate: workOrderFilterState.startDate,
        endDate: workOrderFilterState.endDate,
        productType:
          workOrderFilterState.productType === "ALL"
            ? null
            : workOrderFilterState.productType,
      })
    );
  }, [
    dispatch,
    setWorkOrderFilters,
    clearDeviceWorkOrders,
    clearDevicesPagination,
    workOrderFilterState,
  ]);

  useEffect(() => {
    dispatch(clearAssemblyWorkOrders());
    dispatch(clearWorkOrderPagination());
    dispatch(
      setWorkOrderFilters({
        ...workOrderFilters,
        startDate: workOrderFilterState.startDate,
        endDate: workOrderFilterState.endDate,
        productType:
          workOrderFilterState.productType === "ALL"
            ? null
            : workOrderFilterState.productType,
      })
    );
  }, [
    dispatch,
    clearAssemblyWorkOrders,
    setWorkOrderFilters,
    clearAssembliesPagination,
    workOrderFilterState,
  ]);

  useEffect(() => {
    if (location.pathname.includes("assemblies")) {
      dispatch(setCurrentType("ASSEMBLIES"));
      setBtnStyleType(1);
    } else {
      dispatch(setCurrentType("DEVICES"));
      setBtnStyleType(0);
    }
  }, [setCurrentType]);

  const handleChangeType = (type) => {
    if (type === "DEVICES") {
      setBtnStyleType(0);
      navigate("devices/Analytics");
      window.location.reload(false);
    } else {
      setBtnStyleType(1);
      navigate("assemblies/Analytics");
      window.location.reload(false);
    }
    dispatch(setCurrentType(type));

    setWorkOrderFilters({
      ...workOrderFilterState,
      startDate: null,
      endDate: null,
      productType: null,
    });
  };

  const content = (
    <div className={clsx(classes.root, className)}>
      <nav className={classes.navigation}>
        <List>
          <ListSubheader disableSticky>
            {currentType === "DEVICES" ? (
              <div className="groupButtons">
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ margin: "0px 0" }}
                >
                  <br />
                  Date
                </Typography>

                <TextField
                  id="date"
                  label="From"
                  variant="outlined"
                  type="date"
                  name="startDate"
                  style={{
                    marginTop: "10px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                  }}
                  onChange={handleChange}
                  defaultValue="2018-05-05"
                  className={classes.textField}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  id="date"
                  label="To"
                  name="endDate"
                  variant="outlined"
                  type="date"
                  onChange={handleChange}
                  defaultValue={`${rest.today}`}
                  className={classes.textField}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ backgroundColor: "#f5f5f5", borderRadius: "4px" }}
                />
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ margin: "10px 0", marginTop: "15px" }}
                >
                  <br />
                  Product
                </Typography>
                <FormControl
                  variant="outlined"
                  style={{
                    backgroundColor: "#f5f5f5",
                    minWidth: 150,
                    maxWidth: 200,
                  }}
                >
                  <Select
                    value={workOrderFilterState.productType ?? "ALL"}
                    onChange={handleChangeProductType}
                    inputProps={{
                      name: "productType",
                    }}
                  >
                    <MenuItem value="ALL">ALL</MenuItem>
                    <MenuItem value="SPIROHOME">SPIROHOME PERSONAL</MenuItem>
                    <MenuItem value="SPIROCLINIC">SPIROHOME CLINIC</MenuItem>
                    <MenuItem value="SPIROCLINIC_COMPACT">SPIROCLINIC COMPACT</MenuItem>
                    <MenuItem value="SPIROCLINIC_PRO_HANDPIECE">SPIROCLINIC PRO HANDPIECE</MenuItem>
                    <MenuItem value="SPIROCLINIC_PRO_DOCK">SPIROCLINIC PRO DOCK</MenuItem>
                    <MenuItem value="ISPIRO">ISPIRO ULTRASONIC</MenuItem>
                    <MenuItem value="ISPIRO_PRO">ISPIRO PRO</MenuItem>

                  </Select>
                </FormControl>
              </div>
            ) : (
              <div className="groupButtons">
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ margin: "0px 0" }}
                >
                  <br />
                  Date
                </Typography>

                <TextField
                  id="date"
                  label="From"
                  variant="outlined"
                  type="date"
                  name="startDate"
                  onChange={handleChangeAssemblies}
                  defaultValue="2018-05-05"
                  className={classes.textField}
                  style={{ marginTop: "10px", backgroundColor: "#f5f5f5" }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  id="date"
                  label="To"
                  name="endDate"
                  variant="outlined"
                  type="date"
                  onChange={handleChangeAssemblies}
                  defaultValue={`${rest.today}`}
                  className={classes.textField}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ backgroundColor: "#f5f5f5" }}
                />
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ margin: "10px 0", marginTop: "15px" }}
                >
                  <br />
                  Product
                </Typography>
                <FormControl
                  variant="outlined"
                  style={{
                    backgroundColor: "#f5f5f5",
                    minWidth: 150,
                    maxWidth: 200,
                  }}
                >
                  <Select
                    value={workOrderFilterState.productType ?? "ALL"}
                    onChange={handleChangeProductType}
                    inputProps={{
                      name: "productType",
                    }}
                  >
                    <MenuItem value="ALL">ALL</MenuItem>
                    <MenuItem value="SPIROHOME">SPIROHOME PERSONAL, SPIROHOME CLINIC AND SPIROCLINIC COMPACT</MenuItem>
                    <MenuItem value="SPIROCLINIC_PRO_HANDPIECE">SPIROCLINIC PRO HANDPIECE</MenuItem>
                    <MenuItem value="SPIROCLINIC_PRO_DOCK">SPIROCLINIC PRO DOCK</MenuItem>
                    <MenuItem value="ISPIRO">ISPIRO ULTRASONIC</MenuItem>
                    <MenuItem value="ISPIRO_PRO">ISPIRO PRO</MenuItem>
                  </Select>
                </FormControl>
              </div>
            )}
          </ListSubheader>
        </List>
      </nav>

      <div>
        <Typography className={classes.appVersion} variant="h5">
          {`Version: ${packageJson.version}`}
        </Typography>
      </div>
    </div>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{
            paper: classes.mobileDrawer,
          }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          <nav className={classes.type}>
            <List>
              <ListSubheader disableSticky>
                <div className="groupButtons">
                  <Typography
                    variant="h6"
                    gutterBottom
                    style={{ margin: "10px 0" }}
                  >
                    Types
                  </Typography>
                  <ButtonGroup
                    size="small"
                    aria-label="outlined small button group"
                    variant="contained"
                  >
                    <Button
                      children="DEVICES"
                      color={btnStyleType === 0 ? "primary" : ""}
                      onClick={(event) => {
                        handleChangeType(
                          event.currentTarget.children[0].innerHTML
                        );
                      }}
                    />
                    <Button
                      children="ASSEMBLIES"
                      color={btnStyleType === 1 ? "primary" : ""}
                      onClick={(event) => {
                        handleChangeType(
                          event.currentTarget.children[0].innerHTML
                        );
                      }}
                    />
                  </ButtonGroup>
                  <Divider orientation="horizontal" style={{ height: "2px" }} />
                </div>
              </ListSubheader>
            </List>
          </nav>
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{
            paper: classes.desktopDrawer,
          }}
          open
          variant="persistent"
        >
          <nav className={classes.type}>
            <List>
              <ListSubheader disableSticky>
                <div className="groupButtons">
                  <Typography
                    variant="h6"
                    gutterBottom
                    style={{ margin: "10px 0" }}
                  >
                    Types
                  </Typography>
                  <ButtonGroup
                    size="small"
                    variant="contained"
                    aria-label="outlined small button group"
                  >
                    <Button
                      children="DEVICES"
                      color={btnStyleType === 0 ? "primary" : ""}
                      onClick={(event) => {
                        handleChangeType("DEVICES");
                      }}
                    />
                    <Button
                      children="ASSEMBLIES"
                      color={btnStyleType === 1 ? "primary" : ""}
                      onClick={(event) => {
                        handleChangeType("ASSEMBLIES");
                      }}
                    />
                  </ButtonGroup>
                  <Divider orientation="horizontal" style={{ height: "2px" }} />
                </div>
              </ListSubheader>
            </List>
          </nav>
          {content}
        </Drawer>
      </Hidden>
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    currentType: state.currentTypeState.currentType,
    paginationState: state.paginationState,
    filtersState: state.filtersState,
  };
};

NavBar.propTypes = {
  className: PropTypes.string,
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default connect(mapStateToProps, null)(NavBar);
